import type {
  TastytradeSession,
  TastytradeAccount,
  TastytradeQuote,
} from "@/types/tastytrade";
import type {
  CreateStrategyDto,
  UpdateStrategyDto,
  Strategy,
  StrategyListParams,
  StrategyListResponse,
  StrategyDetailResponse,
  ValidationParams,
  ValidationResult,
} from "@/types/strategy";
import type {
  MarketMetricsResponse,
  MarketDataByTypeResponse,
} from "@/types/market-screener.types";
import type { ScreenerMetricsRow as ScreenerMetricsApiRow } from "@/services/underlyings.service";
import { storageConfig } from "@/config/storage.config";
import type { NetLiqHistoryPoint } from "@/types/domain/market-data";
import type { PaginationMeta } from "@/types/utils";
import type { SymbolSearchCollectionResponse } from "@/types/symbol-search.types";
import type { OptionChainData } from "@/services/option-chain-authenticated.service";

type JsonRecord = Record<string, unknown>;

interface OptionChainRouteData {
  expirations: OptionChainData[];
  underlyingPrice: number;
}

export interface OptionChainRouteResponse {
  success?: boolean;
  symbol?: string;
  data?: OptionChainRouteData;
  count?: number;
  error?: string;
  details?: string;
}

export interface DryRunOrderLeg {
  "instrument-type": string;
  symbol: string;
  quantity: number;
  action: string;
  [key: string]: unknown;
}

export interface DryRunOrderRequest extends JsonRecord {
  "order-type": string;
  "time-in-force": string;
  price?: number;
  "stop-price"?: number;
  legs: [DryRunOrderLeg, ...DryRunOrderLeg[]];
}

export interface OrderValidationResponse extends JsonRecord {
  errors?: string[];
  warnings?: string[];
}

export interface MarginRequirementSummary extends JsonRecord {
  "buying-power-effect"?: number;
  "maintenance-requirement"?: number;
  "initial-requirement"?: number;
}

export interface MarginRequirementsResponse extends JsonRecord {
  "margin-requirement"?: MarginRequirementSummary;
  data?: JsonRecord & { "margin-requirement"?: MarginRequirementSummary };
}

export interface OrderSubmissionResponse extends JsonRecord {
  "order-id"?: string | number;
  status?: string;
}

type PositionAttributeValue = string | number | boolean | null;

interface NetLiqHistoryResponse {
  data?: {
    items?: NetLiqHistoryPoint[];
    pagination?: PaginationMeta;
  };
  items?: NetLiqHistoryPoint[];
}

class ApiClient {
  // Rate limiting cache for net-liq-history
  private netLiqHistoryCache = new Map<
    string,
    { data: NetLiqHistoryPoint[]; timestamp: number }
  >();
  private readonly NET_LIQ_CACHE_DURATION = 60000; // 1 minute cache
  private serverSessionToken: string | null = null;

  // Build a robust error message without assuming JSON body
  private async buildErrorMessage(
    response: Response,
    fallback: string,
  ): Promise<string> {
    // Gateway/service issues first
    if (response.status === 502 || response.status === 503) {
      return `Service temporarily unavailable (${response.status}). Please try again.`;
    }

    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const data = await response.json();
        return data?.error || data?.message || fallback;
      } catch {
        // Fall through to text
      }
    }

    try {
      const text = await response.text();
      // Avoid dumping large HTML into the message
      if (text && text.length < 500) return text;
    } catch {}

    return fallback;
  }

  private getBaseUrl(): string {
    // For server-side requests, we need the full URL
    return typeof window === 'undefined' ? 'http://localhost:3010' : '';
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (typeof window === "undefined" && this.serverSessionToken) {
      headers["Authorization"] = this.serverSessionToken;
    }
    return headers;
  }

  private getFetchOptions(options: RequestInit = {}): RequestInit {
    return {
      ...options,
      credentials: "include" as RequestCredentials, // Always include cookies
    };
  }

  private async fetchWithOAuthRefresh(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const response = await fetch(input, init);
    if (response.status !== 401 || typeof window === "undefined") {
      return response;
    }

    const refreshResponse = await fetch("/api/auth/oauth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (!refreshResponse.ok) {
      return response;
    }

    return fetch(input, init);
  }

  setServerSessionToken(token: string | null) {
    this.serverSessionToken = token || null;
  }


  private resolveUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = this.getBaseUrl().replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  }

  private async request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    const url = this.resolveUrl(path);
    const mergedHeaders: Record<string, string> = {
      ...(this.getHeaders() as Record<string, string>),
      ...((init?.headers as Record<string, string>) || {}),
    };

    const upperMethod = method.toUpperCase();
    const options = this.getFetchOptions({
      ...init,
      method: upperMethod,
      headers: mergedHeaders,
    });

    if (body !== undefined && upperMethod !== "GET" && upperMethod !== "HEAD") {
      if (
        typeof body === "string" ||
        body instanceof URLSearchParams ||
        body instanceof FormData ||
        body instanceof Blob ||
        body instanceof ArrayBuffer
      ) {
        options.body = body as BodyInit;
      } else {
        options.body = JSON.stringify(body);
        if (!mergedHeaders["Content-Type"]) {
          mergedHeaders["Content-Type"] = "application/json";
        }
      }
    }

    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(
          response,
          fallbackMessage ?? `${upperMethod} ${path} failed`,
        ),
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      return (await response.text()) as unknown as T;
    }

    return response.json();
  }

  async get<T = unknown>(
    path: string,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    return this.request<T>("GET", path, undefined, init, fallbackMessage);
  }

  async post<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    return this.request<T>("POST", path, body, init, fallbackMessage);
  }

  async put<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    return this.request<T>("PUT", path, body, init, fallbackMessage);
  }

  async patch<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    return this.request<T>("PATCH", path, body, init, fallbackMessage);
  }

  async delete<T = unknown>(
    path: string,
    init?: RequestInit,
    fallbackMessage?: string,
  ): Promise<T> {
    return this.request<T>("DELETE", path, undefined, init, fallbackMessage);
  }

  async login(
    username: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<{
    session: TastytradeSession;
    accounts: TastytradeAccount[];
  }> {
    const response = await fetch(
      `${this.getBaseUrl()}/api/auth/login`,
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ username, password, rememberMe }),
      }),
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Login failed"));
    }

    const result = await response.json();
    return result;
  }

  async fetchAccounts(): Promise<TastytradeAccount[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/api/auth/accounts`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, "Failed to fetch accounts"),
      );
    }

    const data = await response.json();
    return data.accounts;
  }

  async fetchPositions(accountNumber: string) {
    console.log("ApiClient: fetchPositions called for", accountNumber);

    // Correct route: /api/positions/:accountNumber
    const response = await this.fetchWithOAuthRefresh(
      `${this.getBaseUrl()}/api/positions/${accountNumber}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      const msg = await this.buildErrorMessage(
        response,
        "Failed to fetch positions",
      );
      console.error("ApiClient: Positions fetch failed:", response.status, msg);
      throw new Error(msg);
    }

    return response.json();
  }

  async fetchBalance(accountNumber: string) {
    console.log("ApiClient: fetchBalance called for", accountNumber);

    const response = await this.fetchWithOAuthRefresh(
      `${this.getBaseUrl()}/api/accounts/${accountNumber}/balance`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, "Failed to fetch balance"),
      );
    }

    return response.json();
  }

  async fetchBalanceSnapshots(
    accountNumber: string,
    params?: {
      snapshotDate?: string;
      daysBack?: number;
      startDate?: string;
    },
  ) {
    const searchParams = new URLSearchParams();
    if (params?.snapshotDate)
      searchParams.append("snapshot-date", params.snapshotDate);
    if (params?.daysBack)
      searchParams.append("days-back", params.daysBack.toString());
    if (params?.startDate) searchParams.append("start-date", params.startDate);

    const response = await this.fetchWithOAuthRefresh(
      `${this.getBaseUrl()}/api/accounts/${accountNumber}/balance-snapshots${searchParams.toString() ? `?${searchParams}` : ""}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      const msg = await this.buildErrorMessage(
        response,
        "Failed to fetch balance snapshots",
      );
      console.error(
        "Balance snapshots error:",
        response.status,
        response.url,
        msg,
      );
      throw new Error(msg);
    }

    return response.json();
  }

  async fetchNetLiqHistory(
    accountNumber: string,
    params?: {
      startDate?: string;
      endDate?: string;
      startTime?: string;
      endTime?: string;
      timeBack?: string;
    },
  ): Promise<NetLiqHistoryPoint[]> {
    // Create cache key from parameters
    const cacheKey = `${accountNumber}-${JSON.stringify(params || {})}`;
    const cached = this.netLiqHistoryCache.get(cacheKey);

    // Return cached data if it's less than 1 minute old
    if (cached && Date.now() - cached.timestamp < this.NET_LIQ_CACHE_DURATION) {
      return cached.data;
    }

    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append("start-date", params.startDate);
    if (params?.endDate) searchParams.append("end-date", params.endDate);
    if (params?.startTime) searchParams.append("start-time", params.startTime);
    if (params?.endTime) searchParams.append("end-time", params.endTime);
    if (params?.timeBack) searchParams.append("time-back", params.timeBack);

    try {
      const response = await this.fetchWithOAuthRefresh(
        `/api/accounts/${accountNumber}/net-liq-history${searchParams.toString() ? `?${searchParams}` : ""}`,
        this.getFetchOptions({
          method: "GET",
          headers: this.getHeaders(),
        }),
      );

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          console.warn(
            "Rate limited on net-liq-history, using cached data or returning empty",
          );
          // Return cached data even if stale, or empty data
          if (cached) {
            return cached.data;
          }
          // Return empty data structure to prevent errors
          return [];
        }

        const msg = await this.buildErrorMessage(
          response,
          "Failed to fetch net liquidating value history",
        );
        console.error(
          "Net-liq-history error:",
          response.status,
          response.url,
          msg,
        );
        throw new Error(msg);
      }

      const payload = (await response.json()) as
        | NetLiqHistoryResponse
        | NetLiqHistoryPoint[];
      const items = Array.isArray(payload)
        ? payload
        : payload?.data?.items ?? payload?.items ?? [];

      // Cache the successful response
      this.netLiqHistoryCache.set(cacheKey, {
        data: items,
        timestamp: Date.now(),
      });

      return items;
    } catch (error) {
      console.error("Error fetching net-liq history:", error);
      // Return cached data on error
      if (cached) {
        return cached.data;
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    // Clear local storage and call server logout
    localStorage.removeItem("selected_account");
    
    const response = await fetch(
      `${this.getBaseUrl()}/api/auth/logout`,
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
      }),
    );
    
    if (!response.ok) {
      console.error("Logout failed:", await this.buildErrorMessage(response, "Logout failed"));
    }
  }

  async getOptionChain(symbol: string): Promise<OptionChainRouteResponse> {
    const response = await fetch(
      `/api/option-chains/${symbol}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to fetch option chain"));
    }

    return (await response.json()) as OptionChainRouteResponse;
  }

  async searchSymbols(query: string): Promise<SymbolSearchCollectionResponse> {
    const response = await fetch(
      `/api/symbols/search?q=${encodeURIComponent(query)}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      const msg = await this.buildErrorMessage(response, "Failed to search symbols");
      console.error("Symbol search API error:", response.status, msg);
      throw new Error(msg);
    }

    return (await response.json()) as SymbolSearchCollectionResponse;
  }

  async validateOrder(
    accountNumber: string,
    order: DryRunOrderRequest,
  ): Promise<OrderValidationResponse> {
    const response = await fetch(
      `/api/accounts/${accountNumber}/orders/dry-run`,
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(order),
      }),
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to validate order"));
    }

    return (await response.json()) as OrderValidationResponse;
  }

  // Strategy Builder API methods
  async createStrategy(
    data: CreateStrategyDto,
  ): Promise<{ strategy: Strategy }> {
    const endpoint = storageConfig.getStrategyEndpoint();
    const response = await fetch(endpoint, 
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      })
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to create strategy"));
    }

    return response.json();
  }

  async listStrategies(
    params?: StrategyListParams,
  ): Promise<StrategyListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.active !== undefined)
      searchParams.append("active", params.active.toString());
    if (params?.is0Dte !== undefined)
      searchParams.append("is0Dte", params.is0Dte.toString());

    const endpoint = storageConfig.getStrategyEndpoint();
    const response = await fetch(`${endpoint}?${searchParams}`, 
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      })
    );

    if (!response.ok) {
      const msg = await this.buildErrorMessage(
        response,
        `Failed to list strategies: ${response.status}`,
      );
      console.error("Failed to list strategies:", response.status, msg);
      throw new Error(msg);
    }

    return response.json();
  }

  async getStrategy(id: string): Promise<StrategyDetailResponse> {
    const endpoint =
      storageConfig.strategies === "json"
        ? `/api/strategies/json/${id}`
        : `/api/strategies/${id}`;
    const response = await fetch(endpoint, 
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      })
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Strategy not found");
      }
      throw new Error(await this.buildErrorMessage(response, "Failed to get strategy"));
    }

    return response.json();
  }

  async updateStrategy(
    id: string,
    data: UpdateStrategyDto,
  ): Promise<{ strategy: Strategy }> {
    const endpoint =
      storageConfig.strategies === "json"
        ? `/api/strategies/json/${id}`
        : `/api/strategies/${id}`;
    const response = await fetch(endpoint, 
      this.getFetchOptions({
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      })
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to update strategy"));
    }

    return response.json();
  }

  async deleteStrategy(id: string): Promise<{ success: boolean }> {
    const endpoint =
      storageConfig.strategies === "json"
        ? `/api/strategies/json/${id}`
        : `/api/strategies/${id}`;
    const response = await fetch(endpoint, 
      this.getFetchOptions({
        method: "DELETE",
        headers: this.getHeaders(),
      })
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Strategy not found");
      }
      throw new Error(await this.buildErrorMessage(response, "Failed to delete strategy"));
    }

    return response.json();
  }

  async validateStrategy(
    id: string,
    params: ValidationParams,
  ): Promise<{ validations: ValidationResult[] }> {
    // Use storage config to determine the correct validation endpoint
    const endpoint = storageConfig.getValidationEndpoint(id);
    const response = await fetch(endpoint, 
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(params),
      })
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to validate strategy"));
    }

    return response.json();
  }

  async getMarginRequirements(
    accountNumber: string,
    orders: ReadonlyArray<DryRunOrderRequest>,
  ): Promise<MarginRequirementsResponse> {
    const response = await fetch(
      `/api/accounts/${accountNumber}/margin-requirements`,
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ orders }),
      }),
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to get margin requirements"));
    }

    return (await response.json()) as MarginRequirementsResponse;
  }

  async submitOrder(
    accountNumber: string,
    order: DryRunOrderRequest,
  ): Promise<OrderSubmissionResponse> {
    const response = await fetch(`/api/accounts/${accountNumber}/orders`, 
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(order),
      })
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to submit order"));
    }

    return (await response.json()) as OrderSubmissionResponse;
  }

  async submitComplexOrder(
    accountNumber: string,
    order: Record<string, unknown>,
  ): Promise<OrderSubmissionResponse> {
    const response = await fetch(
      `/api/accounts/${accountNumber}/orders/complex`,
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(order),
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, "Failed to submit complex order"),
      );
    }

    return (await response.json()) as OrderSubmissionResponse;
  }


  async cancelOrder(accountNumber: string, orderId: string | number): Promise<Record<string, unknown>> {
    return this.post(
      "/api/orders/cancel",
      { accountNumber, orderId },
      undefined,
      `Failed to cancel order ${orderId}`,
    );
  }

  async getOrder(accountNumber: string, orderId: string | number): Promise<unknown> {
    const normalized = typeof orderId === "string" ? orderId : String(orderId);
    return this.get(
      `/api/accounts/${accountNumber}/orders/${normalized}`,
      undefined,
      `Failed to fetch order ${normalized}`,
    );
  }

  async updatePositionAttribute(
    accountNumber: string,
    symbol: string,
    attributeType: string,
    attributeValue: PositionAttributeValue,
  ): Promise<JsonRecord> {
    const response = await fetch(`/api/positions/attributes/simple`, 
      this.getFetchOptions({
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({
          account_number: accountNumber,
          symbol: symbol,
          attribute_type: attributeType,
          attribute_value: attributeValue,
        }),
      })
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to update position attribute"));
    }

    return (await response.json()) as JsonRecord;
  }

  async fetchMarketMetrics(
    symbols: string | string[],
    options?: {
      includeExpirations?: boolean;
      maxExpirations?: number;
    },
  ): Promise<MarketMetricsResponse> {
    const symbolsParam = Array.isArray(symbols) ? symbols.join(",") : symbols;
    const searchParams = new URLSearchParams({
      symbols: symbolsParam,
    });

    // Add optional parameters
    if (options?.includeExpirations !== undefined) {
      searchParams.append(
        "includeExpirations",
        options.includeExpirations.toString(),
      );
    }
    if (options?.maxExpirations !== undefined) {
      searchParams.append("maxExpirations", options.maxExpirations.toString());
    }

    const response = await fetch(
      `/api/market-metrics?${searchParams.toString()}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
        cache: "no-store", // Prevent caching
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, `Market metrics fetch failed: ${response.status}`),
      );
    }

    return response.json();
  }

  async fetchMarketDataByType(params: {
    equity?: string | string[];
    equityOption?: string | string[];
    cryptocurrency?: string | string[];
    index?: string | string[];
    future?: string | string[];
    futureOption?: string | string[];
  }): Promise<MarketDataByTypeResponse> {
    const searchParams = new URLSearchParams();

    // Convert arrays to comma-separated strings
    if (params.equity) {
      searchParams.append(
        "equity",
        Array.isArray(params.equity) ? params.equity.join(",") : params.equity,
      );
    }
    if (params.equityOption) {
      searchParams.append(
        "equity-option",
        Array.isArray(params.equityOption)
          ? params.equityOption.join(",")
          : params.equityOption,
      );
    }
    if (params.cryptocurrency) {
      searchParams.append(
        "cryptocurrency",
        Array.isArray(params.cryptocurrency)
          ? params.cryptocurrency.join(",")
          : params.cryptocurrency,
      );
    }
    if (params.index) {
      searchParams.append(
        "index",
        Array.isArray(params.index) ? params.index.join(",") : params.index,
      );
    }
    if (params.future) {
      searchParams.append(
        "future",
        Array.isArray(params.future) ? params.future.join(",") : params.future,
      );
    }
    if (params.futureOption) {
      searchParams.append(
        "future-option",
        Array.isArray(params.futureOption)
          ? params.futureOption.join(",")
          : params.futureOption,
      );
    }

    const response = await fetch(
      `/api/market-data/by-type?${searchParams.toString()}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
        cache: "no-store", // Prevent caching
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, `Market data fetch failed: ${response.status}`),
      );
    }

    return response.json();
  }

  async getUnderlyings(): Promise<
    Array<{
      symbol: string;
      sector: string | null;
      user_defined_sector: string | null;
      asset_type: string;
      active: boolean;
    }>
  > {
    // Fetch all active equity underlyings without limit
    const response = await fetch(
      `/api/underlyings?isActive=true&assetType=EQUITY&limit=20000`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response, "Failed to fetch underlyings"));
    }

    const data = await response.json();
    return data.data || [];
  }

  async getUnderlyingsForScreener(opts: {
    minIvRank?: number;
    sectors?: string[];
    limit?: number;
    requireFresh?: boolean;
    freshWithinHours?: number;
    minVolume?: number | null;
    minMarketCap?: number | null;
    minLiquidityRating?: number | null;
    minLiquidityRank?: number | null;
    minLiquidityValue?: number | null;
    requireOptionsAvailable?: boolean;
  } = {}): Promise<ScreenerMetricsApiRow[]> {
    const sp = new URLSearchParams();
    if (typeof opts.minIvRank === "number") sp.set("minIvRank", String(opts.minIvRank));
    if (typeof opts.limit === "number") sp.set("limit", String(opts.limit));
    if (typeof opts.freshWithinHours === "number") {
      sp.set("freshWithinHours", String(opts.freshWithinHours));
    }
    if (opts.minVolume === null) sp.set("minVolume", "off");
    else if (typeof opts.minVolume === "number") sp.set("minVolume", String(opts.minVolume));
    if (opts.minMarketCap === null) sp.set("minMarketCap", "off");
    else if (typeof opts.minMarketCap === "number") sp.set("minMarketCap", String(opts.minMarketCap));
    if (opts.minLiquidityRating === null) sp.set("minLiquidityRating", "off");
    else if (typeof opts.minLiquidityRating === "number") sp.set("minLiquidityRating", String(opts.minLiquidityRating));
    if (opts.minLiquidityRank === null) sp.set("minLiquidityRank", "off");
    else if (typeof opts.minLiquidityRank === "number") sp.set("minLiquidityRank", String(opts.minLiquidityRank));
    if (opts.minLiquidityValue === null) sp.set("minLiquidityValue", "off");
    else if (typeof opts.minLiquidityValue === "number") sp.set("minLiquidityValue", String(opts.minLiquidityValue));
    if (opts.requireOptionsAvailable === false) sp.set("requireOptionsAvailable", "false");
    if (opts.requireFresh === false) sp.set("requireFresh", "false");
    opts.sectors?.forEach((s) => sp.append("sector", s));

    const url = `/api/underlyings/screener${sp.toString() ? `?${sp}` : ""}`;
    const response = await fetch(
      url,
      this.getFetchOptions({ method: "GET", headers: this.getHeaders() }),
    );
    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, "Failed to fetch screener underlyings"),
      );
    }
    const json = (await response.json()) as { data?: ScreenerMetricsApiRow[] };
    return json.data ?? [];
  }

  // Alias for compatibility with monitoring services
  async getPositions(accountNumber: string) {
    return this.fetchPositions(accountNumber);
  }

  // Get working orders for an account
  async getWorkingOrders(accountNumber: string): Promise<JsonRecord[]> {
    const response = await fetch(
      `${this.getBaseUrl()}/api/accounts/${accountNumber}/orders/working`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(
          response,
          "Failed to fetch working orders",
        ),
      );
    }

    const result = (await response.json()) as JsonRecord & {
      orders?: JsonRecord[];
    };
    return result.orders ?? [];
  }

  // Place a simple order
  async placeOrder(
    accountNumber: string,
    order: DryRunOrderRequest,
  ): Promise<OrderSubmissionResponse> {
    return this.submitOrder(accountNumber, order);
  }

  // Place a complex order (multi-leg)
  async placeComplexOrder(
    accountNumber: string,
    order: DryRunOrderRequest,
  ): Promise<OrderSubmissionResponse> {
    return this.submitOrder(accountNumber, order);
  }

  // Get quote for a symbol
  async getQuote(symbol: string): Promise<TastytradeQuote> {
    const response = await fetch(
      `${this.getBaseUrl()}/api/market-data/quote?symbol=${encodeURIComponent(symbol)}`,
      this.getFetchOptions({
        method: "GET",
        headers: this.getHeaders(),
      }),
    );

    if (!response.ok) {
      throw new Error(
        await this.buildErrorMessage(response, "Failed to fetch quote"),
      );
    }

    return (await response.json()) as TastytradeQuote;
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();

// Add debug logging
if (typeof window !== "undefined") {
  console.log("API Client initialized in browser:", apiClient);
}

export { apiClient };
